import { MapboxStyleLayer } from "fond/types";

/**
 * Validation styles for features that have failed validation.
 *
 * These styles apply to features where the "meta:valid" property is set to "false".
 * Features are automatically validated in simpleSelect mode when using layer-specific
 * validation functions.
 *
 * Invalid features are rendered in red with dashed lines/strokes to make them
 * visually distinct from valid features.
 */
export const validationStyles: MapboxStyleLayer[] = [
  // ===== INVALID POINT STYLES =====

  /**
   * Invalid Point features - red circle fill
   */
  {
    id: "gl-draw-point-invalid",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["!=", "meta", "midpoint"], ["==", "user_meta:valid", "false"]],
    paint: {
      "circle-radius": 8,
      "circle-color": "#ff4444",
      "circle-opacity": 0.2,
    },
  },

  /**
   * Invalid Point features - red stroke/outline
   */
  {
    id: "gl-draw-point-stroke-invalid",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["!=", "meta", "midpoint"], ["==", "user_meta:valid", "false"]],
    paint: {
      "circle-radius": 8,
      "circle-color": "transparent",
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ff0000",
      "circle-stroke-opacity": 0.9,
    },
  },

  // ===== INVALID LINESTRING STYLES =====

  /**
   * Invalid LineString features - red dashed line
   */
  {
    id: "gl-draw-line-invalid",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "user_meta:valid", "false"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#ff0000",
      "line-width": 3,
      "line-dasharray": [2, 2],
      "line-opacity": 0.9,
    },
  },

  // ===== INVALID POLYGON STYLES =====

  /**
   * Invalid Polygon features - red semi-transparent fill
   */
  {
    id: "gl-draw-polygon-fill-invalid",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "user_meta:valid", "false"]],
    paint: {
      "fill-color": "#ff0000",
      "fill-outline-color": "#ff0000",
      "fill-opacity": 0.15,
    },
  },

  /**
   * Invalid Polygon features - red dashed stroke
   */
  {
    id: "gl-draw-polygon-stroke-invalid",
    type: "line",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "user_meta:valid", "false"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#ff0000",
      "line-width": 3,
      "line-dasharray": [2, 2],
      "line-opacity": 0.9,
    },
  },

  // TEMPORARY - Style blockers with dynamic line-offset based on active tier combinations
  // The offset calculation positions lines side-by-side when multiple tiers are active
  {
    id: "gl-draw-blockers-tier1",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "user_layerId", "output/data_prep/blockers"], ["==", "user_Tier1", true]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#ff0000",
      "line-width": 3,
      "line-dasharray": [2, 2],
      // Calculate offset based on: (activeIndex - (totalActive - 1) / 2) * spacing
      // Tier1 is always the first active tier when present (activeIndex = 0)
      "line-offset": [
        "*",
        3, // spacing (same as line-width)
        [
          "-",
          0, // Tier1's active index (always first when present)
          [
            "/",
            [
              "-",
              // Count total active tiers
              [
                "+",
                1, // Tier1 (always 1 since this filter requires it)
                ["case", ["==", ["get", "user_Tier2"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier2_MST"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier3"], true], 1, 0],
              ],
              1,
            ],
            2,
          ],
        ],
      ],
    },
  },
  {
    id: "gl-draw-blockers-tier2",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "user_layerId", "output/data_prep/blockers"], ["==", "user_Tier2", true]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#00ff00",
      "line-width": 3,
      "line-dasharray": [2, 2],
      // Tier2's activeIndex depends on whether Tier1 is also active
      "line-offset": [
        "*",
        3,
        [
          "-",
          ["case", ["==", ["get", "user_Tier1"], true], 1, 0], // activeIndex: 1 if Tier1 is active, else 0
          [
            "/",
            [
              "-",
              // Count total active tiers
              [
                "+",
                ["case", ["==", ["get", "user_Tier1"], true], 1, 0],
                1, // Tier2 (always 1 since this filter requires it)
                ["case", ["==", ["get", "user_Tier2_MST"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier3"], true], 1, 0],
              ],
              1,
            ],
            2,
          ],
        ],
      ],
    },
  },
  {
    id: "gl-draw-blockers-tier2_mst",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "user_layerId", "output/data_prep/blockers"], ["==", "user_Tier2_MST", true]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#fff",
      "line-width": 3,
      "line-dasharray": [2, 2],
      // Tier2_MST's activeIndex depends on whether Tier1 and/or Tier2 are active
      "line-offset": [
        "*",
        3,
        [
          "-",
          ["+", ["case", ["==", ["get", "user_Tier1"], true], 1, 0], ["case", ["==", ["get", "user_Tier2"], true], 1, 0]], // activeIndex: count of earlier tiers
          [
            "/",
            [
              "-",
              // Count total active tiers
              [
                "+",
                ["case", ["==", ["get", "user_Tier1"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier2"], true], 1, 0],
                1, // Tier2_MST (always 1 since this filter requires it)
                ["case", ["==", ["get", "user_Tier3"], true], 1, 0],
              ],
              1,
            ],
            2,
          ],
        ],
      ],
    },
  },
  {
    id: "gl-draw-blockers-tier3",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "user_layerId", "output/data_prep/blockers"], ["==", "user_Tier3", true]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#ff00ff",
      "line-width": 3,
      "line-dasharray": [2, 2],
      // Tier3's activeIndex depends on all earlier tiers
      "line-offset": [
        "*",
        3,
        [
          "-",
          [
            "+",
            ["case", ["==", ["get", "user_Tier1"], true], 1, 0],
            ["case", ["==", ["get", "user_Tier2"], true], 1, 0],
            ["case", ["==", ["get", "user_Tier2_MST"], true], 1, 0],
          ], // activeIndex: count of earlier tiers
          [
            "/",
            [
              "-",
              // Count total active tiers
              [
                "+",
                ["case", ["==", ["get", "user_Tier1"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier2"], true], 1, 0],
                ["case", ["==", ["get", "user_Tier2_MST"], true], 1, 0],
                1, // Tier3 (always 1 since this filter requires it)
              ],
              1,
            ],
            2,
          ],
        ],
      ],
    },
  },
];

/**
 * Get validation styles to be added to the map.
 *
 * These should be added BEFORE normal feature styles so they take priority
 * when rendering invalid features.
 *
 * @example
 * import { validationStyles } from './styles';
 *
 * const styles = [
 *   ...validationStyles,  // Invalid features rendered first
 *   ...normalStyles       // Then normal features
 * ];
 */
export default validationStyles;
